import { SITE_TIME_ZONE } from "@/lib/site-time";
import { createFileRoute } from "@tanstack/react-router";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { ReactNode } from "react";
import { ArrowRight, FileText, Home, MapPin, Phone, Mail, Waves } from "lucide-react";
import { LocaleLink, type PublicPath } from "@/components/site/LocaleLink";
import { Container } from "@/components/site/Container";
import { Overline } from "@/components/site/Overline";
import { business, addressLine, langPageMeta } from "@/content/site";
import { publishedPostsQuery } from "@/lib/blog-queries";
import { publishedProjectsQuery } from "@/lib/projects-queries";
import { listPublishedMunicipalities } from "@/lib/projects.functions";
import { i18n } from "@/i18n";

const PATH = "/sitemap";

const municipalitiesQuery = queryOptions({
  queryKey: ["projects", "published", "municipalities"],
  queryFn: () => listPublishedMunicipalities(),
});

export const Route = createFileRoute("/$lang/sitemap")({
  staticData: { sitemap: true },
  head: ({ params }) => {
    const title = i18n.t("sitemapPage.title");
    const description = i18n.t("sitemapPage.description");
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary" },
        ...langPageMeta(params.lang, PATH).meta,
      ],
      links: langPageMeta(params.lang, PATH).links,
    };
  },
  component: SitemapPage,
});

const servicePages: { to: PublicPath; key: string }[] = [
  { to: "/nieuwbouw", key: "nieuwbouw" },
  { to: "/renovatie", key: "renovatie" },
  { to: "/technieken", key: "technieken" },
  { to: "/warmtepompen-afdekkingen", key: "warmtepompen" },
  { to: "/opbouw-zwembad", key: "opbouw" },
];

const companyPages: { to: PublicPath; key: string }[] = [
  { to: "/", key: "home" },
  { to: "/over-ons", key: "overOns" },
  { to: "/offerte", key: "offerte" },
  { to: "/privacybeleid", key: "privacybeleid" },
  { to: "/cookiebeleid", key: "cookiebeleid" },
];

const linkClass =
  "group flex items-start gap-2 rounded-[4px] py-1.5 text-foreground transition-colors duration-200 ease-out hover:text-brand-deep-water focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring";

function SitemapLink({ to, params, children }: { to: PublicPath; params?: Record<string, string> | undefined; children: ReactNode }) {
  return (
    <li>
      <LocaleLink to={to} {...(params ? { params } : {})} className={linkClass}>
        <ArrowRight aria-hidden="true" strokeWidth={1.75} className="mt-1 h-4 w-4 shrink-0 text-brand-deep-water transition-transform duration-200 ease-out group-hover:translate-x-0.5" />
        <span>{children}</span>
      </LocaleLink>
    </li>
  );
}

function Card({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <section className="rounded-lg border border-border bg-surface p-6 md:p-8">
      <h2 className="flex items-center gap-3 font-display text-xl font-semibold text-foreground">
        <span className="text-brand-deep-water">{icon}</span>
        {title}
      </h2>
      <div className="mt-6">{children}</div>
    </section>
  );
}

function Status({ children, role }: { children: ReactNode; role?: "alert" | "status" }) {
  return <p role={role} className="py-1.5 text-muted-foreground">{children}</p>;
}

function SitemapPage() {
  const { t, i18n: inst } = useTranslation();
  const posts = useQuery(publishedPostsQuery);
  const projects = useQuery(publishedProjectsQuery(inst.language));
  const municipalities = useQuery(municipalitiesQuery);
  const dateFmt = new Intl.DateTimeFormat(inst.language === "fr" ? "fr-BE" : inst.language === "en" ? "en-BE" : "nl-BE", { dateStyle: "medium", timeZone: SITE_TIME_ZONE });
  const iconProps = { "aria-hidden": true, strokeWidth: 1.75, className: "h-6 w-6" } as const;

  return (
    <div className="bg-background pb-16 pt-28 md:pb-[120px] md:pt-40">
      <Container>
        <Overline>{t("sitemapPage.overline")}</Overline>
        <h1 className="mt-4 font-display text-[clamp(2rem,4vw,2.75rem)] font-bold leading-tight text-foreground">{t("sitemapPage.heading")}</h1>
        <p className="mt-6 max-w-[70ch] text-lg text-muted-foreground">{t("sitemapPage.intro")}</p>

        <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <Card icon={<Waves {...iconProps} />} title={t("sitemapPage.services")}>
            <ul>{servicePages.map((p) => <SitemapLink key={p.to} to={p.to}>{t(`sitemapPage.pages.${p.key}`)}</SitemapLink>)}</ul>
          </Card>

          <Card icon={<Home {...iconProps} />} title={t("sitemapPage.company")}>
            <ul>{companyPages.map((p) => <SitemapLink key={p.to} to={p.to}>{t(`sitemapPage.pages.${p.key}`)}</SitemapLink>)}</ul>
            <ul className="mt-6 space-y-3 border-t border-border pt-6 text-foreground">
              <li className="flex items-start gap-2"><MapPin aria-hidden="true" strokeWidth={1.75} className="mt-1 h-4 w-4 shrink-0 text-brand-deep-water" />{addressLine}</li>
              <li><a href={`tel:${business.phoneE164}`} className={linkClass}><Phone aria-hidden="true" strokeWidth={1.75} className="mt-1 h-4 w-4 shrink-0 text-brand-deep-water" />{business.phone}</a></li>
              <li><a href={`mailto:${business.email}`} className={linkClass}><Mail aria-hidden="true" strokeWidth={1.75} className="mt-1 h-4 w-4 shrink-0 text-brand-deep-water" />{business.email}</a></li>
            </ul>
          </Card>

          <Card icon={<MapPin {...iconProps} />} title={t("sitemapPage.projects")}>
            <ul>
              <SitemapLink to="/realisaties">{t("sitemapPage.allProjects")}</SitemapLink>
              {projects.isPending ? <li><Status role="status">{t("sitemapPage.loading")}</Status></li>
                : projects.isError ? <li><Status role="alert">{t("sitemapPage.error")}</Status></li>
                : projects.data.length === 0 ? <li><Status>{t("sitemapPage.emptyProjects")}</Status></li>
                : projects.data.map((p) => <SitemapLink key={p.slug} to="/realisaties/$slug" params={{ slug: p.slug }}>{p.titel}</SitemapLink>)}
            </ul>
            {municipalities.data && municipalities.data.length > 0 && (
              <>
                <h3 className="mt-6 font-display text-base font-semibold text-foreground">{t("sitemapPage.municipalities")}</h3>
                <ul className="mt-2">{municipalities.data.map((m) => <SitemapLink key={m.slug} to="/realisaties/gemeente/$gemeente" params={{ gemeente: m.slug }}>{m.gemeente}</SitemapLink>)}</ul>
              </>
            )}
          </Card>

          <div className="md:col-span-2 lg:col-span-3">
            <Card icon={<FileText {...iconProps} />} title={t("sitemapPage.blog")}>
              <ul className="grid gap-x-6 md:grid-cols-2">
                <SitemapLink to="/blog">{t("sitemapPage.allPosts")}</SitemapLink>
                {posts.isPending ? <li><Status role="status">{t("sitemapPage.loading")}</Status></li>
                  : posts.isError ? <li><Status role="alert">{t("sitemapPage.error")}</Status></li>
                  : posts.data.length === 0 ? <li><Status>{t("sitemapPage.emptyPosts")}</Status></li>
                  : posts.data.map((post) => (
                    <SitemapLink key={post.slug} to="/blog/$slug" params={{ slug: post.slug }}>
                      {post.title} <span className="text-muted-foreground">· {dateFmt.format(new Date(post.datePublished))}</span>
                    </SitemapLink>
                  ))}
              </ul>
            </Card>
          </div>
        </div>
      </Container>
    </div>
  );
}
